import { useEffect, useState } from 'preact/hooks';
import { Icons } from './icons';

// ── Sparkline ─────────────────────────────────────────────
interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
  area?: boolean;
}
export function Sparkline({ data, width = 80, height = 22, color = 'currentColor', area = true }: SparklineProps) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  const pts: [number, number][] = data.map((v, i) => [
    i * step,
    height - ((v - min) / range) * (height - 2) - 1,
  ]);
  const line = 'M' + pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' L');
  const areaP = line + ` L${width},${height} L0,${height} Z`;
  const last = pts[pts.length - 1]!;
  return (
    <svg class="sparkline" width={width} height={height} style={{ color, overflow: 'visible' }}>
      {area && <path class="area" d={areaP} />}
      <path class="line" d={line} />
      <circle cx={last[0]} cy={last[1]} r="1.8" fill="currentColor" />
    </svg>
  );
}

// ── Health pill ─────────────────────────────────────────
const HEALTH_TO_PILL: Record<string, string> = {
  active: 'pill-ok',
  prospect: 'pill-warn',
  prospecting: 'pill-warn',
  new: 'pill-info',
  'at-risk': 'pill-danger',
  stale: 'pill-danger',
  partner: 'pill-violet',
  competitor: 'pill-pink',
  employer: 'pill-muted',
  working: 'pill-info',
};

export function HealthPill({ health }: { health: string }) {
  // Normalize "At Risk" → "at-risk" so the lookup table works for either form.
  const h = (health || 'unknown').toLowerCase().replace(/\s+/g, '-');
  const cls = HEALTH_TO_PILL[h] || 'pill-muted';
  const label = h === 'at-risk' ? 'At risk' : h.charAt(0).toUpperCase() + h.slice(1);
  return (
    <span class={`v2-pill ${cls}`}>
      <span class="dot" />
      {label}
    </span>
  );
}

// ── Channel icon chip ───────────────────────────────────
const CHANNEL_MAP = {
  email: { I: Icons.Email, c: 'var(--ch-email)' },
  call: { I: Icons.Call, c: 'var(--ch-call)' },
  linkedin: { I: Icons.LinkedIn, c: 'var(--ch-linkedin)' },
  inperson: { I: Icons.InPerson, c: 'var(--ch-inperson)' },
  'in-person': { I: Icons.InPerson, c: 'var(--ch-inperson)' },
  text: { I: Icons.Text, c: 'var(--ch-text)' },
  sms: { I: Icons.Text, c: 'var(--ch-text)' },
} as const;

export function ChannelIcon({ channel, size = 14 }: { channel: string; size?: number }) {
  const key = (channel || 'email').toLowerCase() as keyof typeof CHANNEL_MAP;
  const m = CHANNEL_MAP[key] || CHANNEL_MAP.email;
  const Icon = m.I;
  return (
    <span style={{ color: m.c, display: 'inline-flex' }}>
      <Icon size={size} />
    </span>
  );
}

// ── Mini bar ────────────────────────────────────────────
export function MiniBar({ value, max = 100, color = 'var(--accent)', width = 60 }: { value: number; max?: number; color?: string; width?: number }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div class="bar-track" style={{ width, color }}>
      <div class="bar-fill" style={{ width: pct + '%' }} />
    </div>
  );
}

// ── Owed pill ───────────────────────────────────────────
export function OwedPill({ who }: { who: 'Me' | 'Them' | null }) {
  if (!who) return <span style={{ color: 'var(--text-faint)', fontFamily: 'var(--f-mono)', fontSize: 11 }}>—</span>;
  const cls = who === 'Me' ? 'pill-warn' : 'pill-info';
  return (
    <span class={`v2-pill ${cls}`} style={{ fontFamily: 'var(--f-mono)' }}>
      {who === 'Me' ? 'We owe them' : 'They owe us'}
    </span>
  );
}

// ── Counter ─────────────────────────────────────────────
export function Counter({ value, suffix = '', className = '' }: { value: number; suffix?: string; className?: string }) {
  const [displayed, setDisplayed] = useState(value);
  useEffect(() => {
    if (displayed === value) return;
    const diff = value - displayed;
    const dir = Math.sign(diff);
    const step = Math.max(1, Math.abs(Math.round(diff / 18)));
    const id = window.setInterval(() => {
      setDisplayed(prev => {
        const next = prev + dir * step;
        if ((dir > 0 && next >= value) || (dir < 0 && next <= value)) {
          window.clearInterval(id);
          return value;
        }
        return next;
      });
    }, 18);
    return () => window.clearInterval(id);
  }, [value]);
  return <span class={`mono ${className}`}>{displayed}{suffix}</span>;
}

// ── Dot cluster ────────────────────────────────────────
export function DotCluster({ value, size = 10, color = 'var(--accent)' }: { value: number; size?: number; color?: string }) {
  const filled = Math.round((value / 100) * 10);
  return (
    <div style={{ display: 'inline-flex', gap: 2, alignItems: 'center' }}>
      {Array.from({ length: 10 }, (_, i) => (
        <div
          key={i}
          style={{
            width: size / 2.5,
            height: size / 2.5,
            borderRadius: 100,
            background: i < filled ? color : 'rgba(255,255,255,0.08)',
          }}
        />
      ))}
    </div>
  );
}

// ── Evidence quote ─────────────────────────────────────
export function Evidence({ children, source }: { children: any; source?: string }) {
  return (
    <div
      style={{
        fontFamily: 'var(--f-serif)',
        fontStyle: 'italic',
        fontSize: 15,
        lineHeight: 1.45,
        color: 'var(--text-2)',
        padding: '10px 14px',
        borderLeft: '2px solid var(--accent)',
        background: 'var(--accent-tint-08)',
        borderRadius: '0 6px 6px 0',
      }}
    >
      {children}
      {source && (
        <div style={{ marginTop: 6, fontFamily: 'var(--f-mono)', fontSize: 10.5, color: 'var(--text-muted)', fontStyle: 'normal' }}>
          {source}
        </div>
      )}
    </div>
  );
}

// ── Avatar ─────────────────────────────────────────────
const AVATAR_PALETTE = ['#6ea8ff', '#4ade80', '#a78bfa', '#fbbf24', '#f472b6', '#38bdf8', '#f87171'];
export function Avatar({ name, size = 28, seed }: { name: string; size?: number; seed?: string }) {
  const letters = (name || '?').split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const hash = (seed || name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const bg = AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 100,
        background: `linear-gradient(135deg, ${bg}, ${bg}aa)`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--f-mono)',
        fontSize: Math.max(10, size * 0.42),
        fontWeight: 600,
        color: 'rgba(0,0,0,0.7)',
        flexShrink: 0,
        boxShadow: 'inset 0 0 0 0.5px rgba(255,255,255,0.2)',
      }}
    >
      {letters}
    </div>
  );
}
